'use client';

import { Badge } from '@depop/web-ui-kit/Badge';
import { LikeIcon } from '@depop/web-ui-kit/Icons/LikeIcon';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { NavigationLink } from '../NavigationLink';
import { NavigationListItem } from '../NavigationListItem';

import styles from './styles.module.css';

import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useGuestLikedProducts } from '@/modules/guestLikes/hooks/useGuestLikedProducts';
import { useIsGuestLikesEnabled } from '@/modules/guestLikes/hooks/useIsGuestLikesEnabled';
import { getLoginRedirectParams } from '@/modules/login/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function NavigationLikesButton() {
  const t = useTranslations('common');
  const [isAuthenticated, currentUser] = useCurrentUser();
  const pathname = usePathname();
  const username = isAuthenticated ? currentUser?.username : '[username]';
  const guestLikedProducts = useGuestLikedProducts();
  const isGuestLikesVariant = useIsGuestLikesEnabled();
  const guestCount = guestLikedProducts.length;
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const likesPath = `/${username}/likes/`;
  const guestLikesUrl = '/likes/';
  const showGuestCount = isGuestLikesVariant && guestCount > 0;
  function getDisplayBreakpoint() {
    if (isGuestLikesVariant) {
      return 'smUp';
    }
    if (!isAuthenticated) {
      return 'mdUp';
    }
    return undefined;
  }

  function getLinkProps() {
    if (isGuestLikesVariant) {
      return { href: guestLikesUrl, isActive: pathname === guestLikesUrl };
    }
    if (!isAuthenticated) {
      return {
        href: `/login/${getLoginRedirectParams(likesPath)}`,
        isActive: false,
      };
    }
    return {
      href: `${likesPath}?isMine=true`,
      isActive: pathname?.endsWith(likesPath),
    };
  }

  function handleLikesClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.NAVBAR_LIKES_ACTION);
  }

  return (
    <NavigationListItem
      handleNavigationListItemClick={handleLikesClick}
      displayBreakpoint={getDisplayBreakpoint()}
    >
      <NavigationLink
        {...getLinkProps()}
        aria-label={
          showGuestCount
            ? t('Nav.LikesWithCount', { count: guestCount })
            : t('Nav.Likes')
        }
        className={isGuestLikesVariant ? styles.linkContainer : undefined}
        isCompact={isGuestLikesVariant}
      >
        <LikeIcon size={28} aria-hidden="true" />
        {showGuestCount && (
          <Badge
            className={styles.likeCount}
            count={guestCount}
            aria-hidden="true"
          />
        )}
      </NavigationLink>
    </NavigationListItem>
  );
}
